import { VirtualTableThCell, type SortType, type TableColumnProps } from './VirtualTableThCell'

import { VirtualTableHeader } from './VirtualTableHeader'

import { VirtualTableRow } from './VirtualTableRow'
import { VirtualTableCell } from './VirtualTableCell'
import { deepClone, DefineComponentProps } from 'utils'
import { VirtualList, type VirtualListExpose } from '../VirtualList/VirtualList'
import { EmptyData } from '../EmptyData'
import './styles/virtual-table.scss'
import { FullScreenMinHeight } from '../FullScreenMinHeight'

export type SortChangeParams = {
  prop: string
  sortType: SortType
}

interface TableProps {
  data: Record<string, any>[]
  columns: (TableColumnProps | false)[]
  rowHeight?: number
  expandRowKeys?: string[]
  rowKey?: string
  onRowClick?: (row: any) => void
  pagination?: boolean
  loading?: boolean
  defaultSortMethod?: (a: any, b: any) => number
  isLazyLoad?: boolean
  onSortChange?: (params: SortChangeParams) => void
  root?: HTMLElement
}

export const VirtualTable = defineComponent<TableProps>({
  props: DefineComponentProps<TableProps>([
    'data',
    'columns',
    'rowHeight',
    'expandRowKeys',
    'rowKey',
    'onRowClick',
    'pagination',
    'loading',
    'defaultSortMethod',
    'isLazyLoad',
    'onSortChange',
    'root',
  ]),
  setup(props) {
    const rowHeight = computed(() => {
      return props.rowHeight || 45
    })
    const columns = computed<TableColumnProps[]>(() => {
      return props.columns.filter((column) => column && column.type !== 'expand') as TableColumnProps[]
    })
    const expandColumns = computed<TableColumnProps | null>(() => {
      return props.columns.find((column) => column && column.type === 'expand') as TableColumnProps | null
    })

    const virtualListRef = ref<VirtualListExpose>()

    const currentSortProp = ref<string>('')
    const currentSortType = ref<SortType>('normal')

    const sortData = computed(() => {
      if (currentSortType.value === 'normal') return deepClone(props.data)
      const column = props.columns.find((column) => column && column.prop === currentSortProp.value)
      const data = deepClone(props.data)
      data.sort((a, b) => {
        if (column && column.sortable === 'custom') {
          return 0
        }
        if (props.defaultSortMethod && currentSortType.value === 'normal') {
          return props.defaultSortMethod(a, b)
        }
        if (column && column.sortMethod) {
          const sortValue = column.sortMethod(a, b)
          if (currentSortType.value === 'ascending') {
            return sortValue
          } else {
            return -sortValue
          }
        }
        if (currentSortProp.value) {
          const valueA = a[currentSortProp.value]
          const valueB = b[currentSortProp.value]
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return currentSortType.value === 'ascending' ? valueA - valueB : valueB - valueA
          }
        }
        return 0
      })
      return data
    })

    const handleSortChange = (sortType: SortType, prop: string) => {
      currentSortProp.value = prop
      currentSortType.value = sortType
      props.onSortChange?.({ prop, sortType })
    }

    const bodyRef = ref<HTMLDivElement>()

    return () => (
      <div class={'virtual-table'}>
        <VirtualTableHeader>
          {columns.value.map((column) => (
            <VirtualTableThCell {...column} onSortChange={handleSortChange} currentSortProp={currentSortProp.value} />
          ))}
        </VirtualTableHeader>
        {sortData.value.length > 0 && (
          <div class="virtual-table__body" ref={bodyRef}>
            <VirtualList
              itemMinHeight={rowHeight.value}
              data={sortData.value}
              ref={virtualListRef}
              rowKey={props.rowKey}
              isLazyLoad={props.isLazyLoad}
              root={bodyRef.value || props.root}
            >
              {{
                default: (data: { data: Record<string, any>; index: number }) => {
                  const { data: item, index } = data
                  return (
                    <>
                      <VirtualTableRow height={rowHeight.value} onClick={() => props.onRowClick?.(item)}>
                        {columns.value.map((column, cellIndex) => {
                          return <VirtualTableCell column={column} data={item} key={`${index}-${cellIndex}`} />
                        })}
                      </VirtualTableRow>
                      {expandColumns.value && props.rowKey && props.expandRowKeys?.includes(String(item[props.rowKey])) && (
                        <div class="expand-row-wrapper">
                          <VirtualTableCell column={expandColumns.value} data={item} />
                        </div>
                      )}
                    </>
                  )
                },
              }}
            </VirtualList>
          </div>
        )}

        {!props.loading && props.data.length === 0 && (
          <div class="virtual-table__empty">
            <FullScreenMinHeight>
              <EmptyData />
            </FullScreenMinHeight>
          </div>
        )}
      </div>
    )
  },
})
